import { useEffect, useRef, useState } from 'react'
import api from '@/lib/api'
import { cn } from '@/lib/utils'
import { ChevronsUpDown } from 'lucide-react'

/**
 * Combobox com busca assíncrona no backend (ideal para FKs grandes,
 * ex.: municípios, tributação nacional). Busca em /endpoint?q=...
 */
export default function AsyncSelect({
  endpoint,
  value,
  onChange,
  optionValue = 'id',
  optionLabel = 'nome',
  placeholder = 'Selecione...',
  initialLabel = '',
  params,
  renderOption,
  /** Monta o texto da opção/seleção a partir do registro (ex.: `Chapecó/SC`). */
  formatLabel,
  limit = 20,
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState([])
  const [label, setLabel] = useState(initialLabel)
  const [loading, setLoading] = useState(false)
  const boxRef = useRef(null)

  useEffect(() => setLabel(initialLabel), [initialLabel])

  // Quando o pai limpa o valor (ex.: botão "Limpar" dos filtros), o rótulo
  // exibido também precisa voltar ao placeholder.
  useEffect(() => {
    if (value === '' || value === null || value === undefined) setLabel('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  // Fecha ao clicar fora.
  useEffect(() => {
    function onClick(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  // `params` costuma ser um literal recriado a cada render: compara por valor.
  const paramsKey = JSON.stringify(params || {})

  // Busca com debounce quando aberto.
  useEffect(() => {
    if (!open) return
    const t = setTimeout(async () => {
      setLoading(true)
      try {
        const { data } = await api.get(endpoint, { params: { ...JSON.parse(paramsKey), q: query, limit } })
        setOptions(data.data || [])
      } finally {
        setLoading(false)
      }
    }, 250)
    return () => clearTimeout(t)
  }, [query, open, endpoint, paramsKey, limit])

  function choose(opt) {
    onChange(opt[optionValue])
    setLabel(formatLabel ? formatLabel(opt) : opt[optionLabel])
    setOpen(false)
    setQuery('')
  }

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-md border border-border bg-input px-3 py-2 text-sm',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        )}
      >
        <span className={cn('truncate', !label && 'text-muted-foreground')}>{label || placeholder}</span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar..."
            className="w-full px-3 py-2 text-sm border-b border-border bg-transparent focus:outline-none"
          />
          <ul className={cn('overflow-auto py-1', renderOption ? 'max-h-80' : 'max-h-56')}>
            {loading && <li className="px-3 py-2 text-sm text-muted-foreground">Buscando...</li>}
            {!loading && options.length === 0 && (
              <li className="px-3 py-2 text-sm text-muted-foreground">Nenhum resultado.</li>
            )}
            {options.map((opt) => (
              <li key={opt[optionValue]}>
                <button
                  type="button"
                  onClick={() => choose(opt)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-accent/60"
                >
                  {renderOption ? renderOption(opt) : (formatLabel ? formatLabel(opt) : opt[optionLabel])}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
